import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, clientsApi, initCsrf } from '../api/client';

interface ClientOffice {
  id: string;
  name: string;
  domicileState: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  firmName: string;
  advisorType: string;
  clients: ClientOffice[];
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  activeClient: ClientOffice | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    firmName: string;
    advisorType: string;
  }) => Promise<void>;
  logout: () => void;
  setActiveClient: (client: ClientOffice) => void;
  addClient: (data: { name: string; domicileState: string }) => Promise<ClientOffice>;
  refreshClients: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [activeClient, setActiveClientState] = useState<ClientOffice | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session on mount — cookie holds the JWT, so validate via /auth/me
  useEffect(() => {
    async function restoreSession() {
      // Initialize CSRF token first
      await initCsrf();

      // Try to restore from localStorage cache first for instant UI
      // savedUser cache handled by server response below
      const savedClientId = localStorage.getItem('osprey_active_office');

      try {
        // Validate session against server (cookie sent automatically)
        const response = await authApi.me();
        const serverUser = response.data.data;
        const userData: User = {
          id: serverUser.id,
          email: serverUser.email,
          firstName: serverUser.firstName,
          lastName: serverUser.lastName,
          firmName: serverUser.firmName,
          advisorType: serverUser.advisorType,
          clients: serverUser.clients || [],
        };
        setUser(userData);
        localStorage.setItem('osprey_user', JSON.stringify(userData));

        // Restore active client
        if (savedClientId && userData.clients?.length) {
          const found = userData.clients.find((c) => c.id === savedClientId);
          if (found) {
            setActiveClientState(found);
          } else if (userData.clients.length > 0) {
            setActiveClientState(userData.clients[0]);
            localStorage.setItem('osprey_active_office', userData.clients[0].id);
          }
        } else if (userData.clients?.length) {
          setActiveClientState(userData.clients[0]);
          localStorage.setItem('osprey_active_office', userData.clients[0].id);
        }
      } catch {
        // No valid cookie/session — clear local state
        localStorage.removeItem('osprey_user');
        localStorage.removeItem('osprey_active_office');
        setUser(null);
        setActiveClientState(null);
      }
      setIsLoading(false);
    }
    restoreSession();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login({ email, password });
    const { user: userData } = response.data.data;
    // Tokens are now in HttpOnly cookies — just cache user data locally
    localStorage.setItem('osprey_user', JSON.stringify(userData));

    // Re-fetch CSRF token (session changed)
    await initCsrf();

    setUser(userData);

    // Auto-select client
    if (userData.clients?.length === 1) {
      setActiveClientState(userData.clients[0]);
      localStorage.setItem('osprey_active_office', userData.clients[0].id);
    } else if (userData.clients?.length > 1) {
      const savedId = localStorage.getItem('osprey_active_office');
      const found = savedId ? userData.clients.find((c: ClientOffice) => c.id === savedId) : null;
      if (found) {
        setActiveClientState(found);
      } else {
        setActiveClientState(userData.clients[0]);
        localStorage.setItem('osprey_active_office', userData.clients[0].id);
      }
    }
  }, []);

  const register = useCallback(async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    firmName: string;
    advisorType: string;
  }) => {
    const response = await authApi.register(data);
    const { user: userData } = response.data.data;
    // Tokens are now in HttpOnly cookies — just cache user data locally
    localStorage.setItem('osprey_user', JSON.stringify(userData));

    // Re-fetch CSRF token (session changed)
    await initCsrf();

    setUser(userData);
    // No clients after registration — advisor adds them next
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Even if server call fails, clear local state
    }
    localStorage.removeItem('osprey_user');
    localStorage.removeItem('osprey_active_office');
    setUser(null);
    setActiveClientState(null);
  }, []);

  const setActiveClient = useCallback((client: ClientOffice) => {
    setActiveClientState(client);
    localStorage.setItem('osprey_active_office', client.id);
  }, []);

  const addClient = useCallback(async (data: { name: string; domicileState: string }) => {
    const response = await clientsApi.create(data);
    const newClient: ClientOffice = {
      id: response.data.data.id,
      name: response.data.data.name,
      domicileState: response.data.data.domicile_state,
    };

    // Update user's client list
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, clients: [...(prev.clients || []), newClient] };
      localStorage.setItem('osprey_user', JSON.stringify(updated));
      return updated;
    });

    // Auto-select the new client
    setActiveClientState(newClient);
    localStorage.setItem('osprey_active_office', newClient.id);

    return newClient;
  }, []);

  const refreshClients = useCallback(async () => {
    try {
      const response = await clientsApi.list();
      const clients: ClientOffice[] = response.data.data.map((c: any) => ({
        id: c.id,
        name: c.name,
        domicileState: c.domicile_state,
      }));

      setUser((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, clients };
        localStorage.setItem('osprey_user', JSON.stringify(updated));
        return updated;
      });
    } catch {
      // Silently fail — non-critical
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        activeClient,
        login,
        register,
        logout,
        setActiveClient,
        addClient,
        refreshClients,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
